import { ArrowLeft, Play, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Avatar } from '../../components/Avatar.tsx';
import { ApiError } from '../../lib/api.ts';
import { getCourseCard } from '../../lib/courses.ts';
import type { RoundPlayer } from '../../lib/rounds.ts';
import { answerRound, getRound } from '../../lib/rounds.ts';

/* Rejoindre le tour de quelqu'un.
 *
 * ── Le départ se choisit ici, pas après ────────────────────────────────────
 *
 * C'est lui qui fixe les distances du carnet, donc les clubs proposés sur les
 * dix-huit trous. Accepter d'abord et choisir ensuite ferait entrer dans le
 * tour avec un écran de jeu qui propose des clubs faux, et un premier départ
 * joué de travers ne se rejoue pas.
 *
 * Sur un même tour, chacun part de sa marque : l'invité ne récupère pas celle
 * de l'hôte.
 */

export function RoundJoin() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [nom, setNom] = useState('');
    const [joueurs, setJoueurs] = useState<RoundPlayer[]>([]);
    const [tees, setTees] = useState<{ id: string; label: string }[]>([]);
    const [teeChoisi, setTeeChoisi] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        void getRound(id)
            .then(async (r) => {
                if (!r.round) throw new ApiError(404, 'Tour introuvable');
                setNom(r.round.courseName);
                setJoueurs(r.players);
                const c = await getCourseCard(r.round.courseId);
                setTees(c.tees.map((t) => ({ id: t.id, label: t.label })));
                setTeeChoisi(c.tees[0]?.id ?? null);
            })
            .catch((e) => setError(e instanceof ApiError ? e.message : 'Chargement impossible'))
            .finally(() => setLoading(false));
    }, [id]);

    const repondre = async (accept: boolean): Promise<void> => {
        if (!id) return;
        setBusy(true);
        setError(null);
        try {
            await answerRound(id, accept, accept ? teeChoisi : null);
            navigate(accept ? '/parcours/jouer' : '/parcours');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Réponse impossible');
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }

    const hote = joueurs[0] ?? null;

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/parcours" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Parcours
                </Link>
                <h1 className="text-2xl font-bold tracking-tight">{nom}</h1>
                {hote && (
                    <p className="mt-1 text-sm opacity-70">
                        {hote.displayName} vous invite à jouer.
                    </p>
                )}
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {joueurs.length > 0 && (
                <section className="rounded-box bg-base-200 p-4">
                    <h2 className="text-sm font-semibold">Sur le départ</h2>
                    <ul className="mt-2 space-y-2">
                        {joueurs.map((j) => (
                            <li key={j.userId} className="flex items-center gap-3">
                                <Avatar name={j.displayName} avatarUrl={j.avatarUrl} size={40}
                                    handicap={j.handicapIndex} />
                                <span className="min-w-0 flex-1 truncate font-semibold">
                                    {j.displayName}
                                </span>
                                <span className="shrink-0 text-xs opacity-60">
                                    {j.status === 'accepted' ? j.teeLabel ?? 'sans départ' : 'invité'}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section className="rounded-box bg-base-200 p-4">
                <h2 className="text-sm font-semibold">Votre départ</h2>
                {tees.length === 0 ? (
                    <p className="mt-1 text-xs opacity-70">
                        Ce parcours n'a pas de départ saisi : les distances, et donc
                        les clubs proposés, ne seront pas disponibles.
                    </p>
                ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {tees.map((t) => (
                            <button key={t.id} type="button" onClick={() => setTeeChoisi(t.id)}
                                aria-pressed={teeChoisi === t.id}
                                className={`h-14 rounded-field px-5 text-base font-bold transition-colors tap-target ${
                                    teeChoisi === t.id
                                        ? 'bg-primary text-primary-content'
                                        : 'bg-base-100 hover:bg-base-300'
                                }`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <button type="button" disabled={busy} onClick={() => void repondre(true)}
                className="btn btn-primary btn-block h-16 gap-2 text-lg font-bold tap-target">
                <Play size={22} /> Rejoindre la partie
            </button>
            <button type="button" disabled={busy} onClick={() => void repondre(false)}
                className="btn btn-ghost btn-block gap-2 tap-target">
                <X size={16} /> Décliner
            </button>
        </div>
    );
}
